import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface DaySkip {
  day: number;
  skips: number;
}

interface MonthlyMealSkipTrendsProps {
  data?: DaySkip[];
  daysInMonth?: number;
  avgSkipsPerDay?: number;
}

const buildDefault = (n: number): DaySkip[] =>
  Array.from({ length: n }, (_, i) => ({
    day: i + 1,
    skips: Math.max(0, Math.round(3 + Math.sin(i * 0.6) * 2 + (i % 3 === 0 ? 1 : 0))),
  }));

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[6px] px-3 py-2 shadow text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1">{label}</p>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full inline-block bg-[#8FCEF5]" />
        <span className="text-[#1F1F1F] font-medium">{payload[0].value} skips</span>
      </div>
    </div>
  );
};

function computeSkipYAxis(data: DaySkip[]): { domain: [number, number]; ticks: number[] } {
  const maxVal = data.reduce((m, d) => Math.max(m, d.skips ?? 0), 0);
  if (maxVal <= 0) return { domain: [0, 4], ticks: [0, 1, 2, 3, 4] };
  const step = Math.max(1, Math.ceil(maxVal / 4));
  const domainMax = step * 4;
  const ticks = Array.from({ length: 5 }, (_, i) => i * step);
  return { domain: [0, domainMax], ticks };
}

const MonthlyMealSkipTrends: React.FC<MonthlyMealSkipTrendsProps> = ({
  data,
  daysInMonth = 30,
  avgSkipsPerDay = 2,
}) => {
  const chartData = data ?? buildDefault(daysInMonth);
  const { domain, ticks } = computeSkipYAxis(chartData);

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Meal Skip Trends
        </span>
      </div>

      <div className="flex-1">
        <ResponsiveContainer width="100%" height={170}>
          <LineChart data={chartData} margin={{ top: 12, right: 12, left: -22, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#E5E7EB", strokeDasharray: "3 3" }} />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              interval={0}
              tick={{ fontSize: 10, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              domain={domain}
              ticks={ticks}
            />
            <Line
              type="monotone"
              dataKey="skips"
              stroke="#8FCEF5"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="text-center text-[11px] 3xl:text-[12px] text-[#8D8D8D] font-inter mt-2">
        Avg skips : <span className="text-[#1F1F1F] font-medium">{avgSkipsPerDay} per day</span>
      </div>
    </div>
  );
};

export default MonthlyMealSkipTrends;
